import { useForm, yupResolver } from '@mantine/form';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { Button, Checkbox, Input } from 'ui';

import { authenticate, isStrongPassword } from '../../core/utils';
import appSettings from '../../app-settings';
import { TAX_ID_REGEX } from '../../core/constants';

const { redirectUri, appClient } = appSettings;

const initialValues = {
  organizationName: '',
  taxId: '',
  legalAddress: '',
  email: '',
  password: '',
  agreeToTerms: false,
};

const SignupForm = ({
  disabled,
  loading,
  onSubmit,
  displayLoginPrompt = true,
  displayOrgFields = false,
  termsFieldLabel = (
    <>
      {' '}
      I agree to the&nbsp;
      <a href="https://www.donorspring.com/terms" target="_blank" rel="noreferrer" className="text-primary-700">
        Terms
      </a>
      &nbsp;and&nbsp;
      <a href="https://www.donorspring.com/privacy" target="_blank" rel="noreferrer" className="text-primary-700">
        Privacy Policy
      </a>
    </>
  ),
}) => {
  const validationSchema = yup.object({
    email: yup.string().email('Invalid email address.').required('Email is required.'),
    password: yup
      .string()
      .test(
        'strong-password',
        'Password needs to be at least 8 characters long, contain at least 1 number, 1 special character, 1 uppercase letter and 1 lowercase letter.',
        (value) => isStrongPassword(value),
      )
      .required('Password is required.'),
    agreeToTerms: yup.boolean().oneOf([true], 'You must agree to the terms and privacy policy.'),
    // Only require these fields if displayOrgFields is true
    taxId: displayOrgFields
      ? yup.string().matches(TAX_ID_REGEX, 'Invalid tax ID.').required('Tax ID is required.')
      : yup.string().notRequired(),
    organizationName: displayOrgFields
      ? yup.string().min(2, 'Invalid name.').required('Organization Name is required.')
      : yup.string().notRequired(),
  });

  const form = useForm({
    initialValues,
    validate: yupResolver(validationSchema),
    validateInputOnBlur: true,
  });
  const navigate = useNavigate();

  return (
    <div className="size-full overflow-y-auto py-8">
      <form
        noValidate
        className="flex min-h-full w-full flex-col items-center justify-center"
        onSubmit={form.onSubmit(onSubmit)}
      >
        <div className="max-w-[336px] space-y-8">
          <div className="space-y-4">
            <h1 className="text-h2 font-semibold">Welcome to DonorSpring</h1>
            <p className="text-lg">Sign up with your organization information, email and password</p>
            {displayLoginPrompt && (
              <p className="text-lg">
                Already have an account?{' '}
                <button
                  onClick={() => authenticate(redirectUri, appClient, navigate, false)}
                  className="text-primary-700"
                >
                  Log in
                </button>
              </p>
            )}
          </div>

          <div className="space-y-4">
            {displayOrgFields && (
              <>
                <Input
                  id="organization-name"
                  label="Organization Name"
                  type="text"
                  {...form.getInputProps('organizationName')}
                  required
                  error={!!form.errors.organizationName}
                  helperText={form.errors.organizationName}
                  size="xl"
                  disabled={disabled}
                />

                <Input
                  id="tax-id"
                  label="EIN / Tax ID"
                  type="text"
                  {...form.getInputProps('taxId')}
                  required
                  error={!!form.errors.taxId}
                  helperText={form.errors.taxId}
                  size="xl"
                  disabled={disabled}
                />
              </>
            )}

            <Input
              id="email"
              label="Email"
              type="email"
              {...form.getInputProps('email')}
              required
              error={!!form.errors.email}
              helperText={form.errors.email}
              size="xl"
              disabled={disabled}
            />

            <Input
              id="password"
              label="Password"
              type="password"
              {...form.getInputProps('password')}
              required
              error={!!form.errors.password}
              helperText={form.errors.password}
              size="xl"
              disabled={disabled}
            />

            <Checkbox
              id="agree-to-terms"
              label={
                <label htmlFor="agree-to-terms" className="text-base">
                  {termsFieldLabel}
                </label>
              }
              checked={form.values.agreeToTerms}
              onChange={(e) => form.setFieldValue('agreeToTerms', e.target.checked)}
              error={!!form.errors.agreeToTerms}
              helperText={form.errors.agreeToTerms}
            />
          </div>

          <Button
            title="Sign Up"
            color="primary"
            size="xl"
            className="!block w-full"
            type="submit"
            disabled={disabled}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

SignupForm.propTypes = {
  disabled: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  displayLoginPrompt: PropTypes.bool,
  displayOrgFields: PropTypes.bool,
  termsFieldLabel: PropTypes.node,
};

export default SignupForm;
